import useSWR from "swr";

import { hasSmartPricingSearchParam } from "../utils/has_smart_pricing_search_param";

export enum DynamicPricingAdjustmentType {
  Percentage = "percentage_adjustment",
  Amount = "amount_adjustment",
}

interface DynamicPricingOption {
  unitPrice: number;
  adjustmentType: DynamicPricingAdjustmentType;
  adjustmentValue: number;
}

export interface DynamicPricingBulkHour extends DynamicPricingOption {
  type: "BulkHour";
  minBookingUnit: number;
}

interface DynamicPricingStartTime extends DynamicPricingOption {
  type: "StartTime";
  startTime: string;
  endTime: string;
}

interface DynamicRates {
  date: string;
  currentUnitPrice: number;
  originalUnitPrice: number;
  options: (DynamicPricingBulkHour | DynamicPricingStartTime)[];
}

export function useDynamicRatesQuery(
  spaceId?: string,
  date?: string,
  unitPrice?: number,
  spaceLayoutId?: string
): { data: DynamicRates | undefined } {
  const qs = buildDynamicRatesQueryString({ date, unitPrice, spaceLayoutId });

  const { data } = useSWR(
    spaceId && date && unitPrice
      ? `/api/v2/spaces/${spaceId}/dynamic_rates/better_hour_rates?&${qs}`
      : null
  );

  return { data: toDynamicRates(data) };
}

/**
 * The single builder for a better-hour-rates price lookup, shared by every
 * caller that issues one. The layout matters because the endpoint prices a
 * layout-priced space against the chosen layout and rejects the request outright
 * without one; the smart pricing opt-in matters because every other rate lookup
 * on the page forwards it, and a picker priced off a different engine
 * contradicts them. Sharing the builder keeps the callers on a single SWR cache
 * key for the same inputs rather than fetching the same URL twice.
 */
export function buildDynamicRatesQueryString({
  date,
  unitPrice,
  spaceLayoutId,
}: {
  date?: string;
  unitPrice?: number;
  spaceLayoutId?: string;
}) {
  const params = new URLSearchParams();
  if (date) {
    params.set("date", date);
  }
  if (spaceLayoutId) {
    params.set("space_layout_id", spaceLayoutId);
  }
  if (hasSmartPricingSearchParam()) {
    params.set("smart_pricing", "true");
  }
  if (unitPrice) {
    params.set("current_unit_price", unitPrice.toString());
  }

  return params.toString();
}

function toDynamicRates(data?: any): DynamicRates | undefined {
  if (data) {
    return {
      date: data.date,
      currentUnitPrice: data.current_unit_price,
      originalUnitPrice: data.original_unit_price,
      options: data.options?.map(toDynamicRatesOption),
    };
  }
}

function toDynamicRatesOption(
  data?: any
): DynamicPricingBulkHour | DynamicPricingStartTime | undefined {
  if (data.min_booking_unit) {
    return {
      type: "BulkHour",
      unitPrice: data.unit_price,
      adjustmentType: data.adjustment_type,
      adjustmentValue: Number(data.adjustment_value),
      minBookingUnit: Number(data.min_booking_unit),
    };
  } else if (data.start_time && data.end_time) {
    return {
      type: "StartTime",
      unitPrice: data.unit_price,
      adjustmentType: data.adjustment_type,
      adjustmentValue: Number(data.adjustment_value),
      startTime: data.start_time,
      endTime: data.end_time,
    };
  }
}
